import { GridApi } from 'ag-grid-community';
import React, { useCallback, useRef, useState } from 'react';
import type { MemberSubsetDraft, useMemberSubsetDraft } from 'src/components/star-grid/hooks/subset-editor/useMemberSubsetDraft';
import { createSubsetView, updateSubsetView } from 'src/components/star-grid/services/starGridService';
import type { PnlSubsetViewSummary } from 'src/components/star-grid/starGridTypes';
import { allPositionKeys, MemberIndex, MemberRow, MemberSubsetOp } from 'src/components/star-grid/utils/subset-editor/memberSubset';
import {
  collapseAllKeys,
  DimensionHierarchyPair,
  expandAllKeys,
  expandedRowUids,
  selectedRowUids,
  splitByExpansion
} from 'src/components/star-grid/utils/subset-editor/memberSubsetEditor';
import { entriesToSave, visibleForSave } from 'src/components/star-grid/utils/subset-editor/savedSubsetViews';

type DraftActions = Pick<
  ReturnType<typeof useMemberSubsetDraft>,
  'applyOp' | 'remove' | 'addParents' | 'expandAll' | 'collapseAll' | 'setChecked' | 'showAll' | 'removeAll'
>;

export interface SubsetSaveTarget {
  readonly aliasPair: DimensionHierarchyPair | undefined;
  readonly effectiveAlias: string | null;
  readonly viewId: number | null;
  readonly setViews: React.Dispatch<React.SetStateAction<readonly PnlSubsetViewSummary[]>>;
  readonly setViewId: React.Dispatch<React.SetStateAction<number | null>>;
}

export type SubsetSaveMode = 'save' | 'saveAs';

/**
 * What the toolbar, the grid's context menu and the Save dialog do to the view.
 *
 * Every callback the grid receives is memoized: AG Grid folds them into grid options, so a fresh identity
 * is an option change it reconciles on every render, which is what made the rows flicker.
 */
export const useSubsetActions = (
  index: MemberIndex,
  draft: MemberSubsetDraft,
  displayRows: readonly MemberRow[],
  gridRows: readonly MemberRow[],
  memberKeys: readonly string[],
  { applyOp, remove, addParents, expandAll, collapseAll, setChecked, showAll, removeAll }: DraftActions,
  { aliasPair, effectiveAlias, viewId, setViews, setViewId }: SubsetSaveTarget
) => {
  // Select All, Clear and Remove drive the GRID's selection; it flows back into the draft from there.
  const gridApiRef = useRef<GridApi<MemberRow> | null>(null);
  const handleApiReady = useCallback((api: GridApi<MemberRow>) => {
    gridApiRef.current = api;
  }, []);

  const runOp = useCallback((op: MemberSubsetOp, selected: readonly string[]): void => applyOp(op, selected, index), [applyOp, index]);
  // The full list, not the searched one: descendants and survivors are about the rows that exist.
  const handleRemove = useCallback(
    (uids: readonly string[], withDescendants: boolean) => remove(uids, withDescendants, gridRows),
    [remove, gridRows]
  );
  const handleAddParents = useCallback((key: string) => addParents(key, index), [addParents, index]);
  const handleExpandAll = useCallback(
    () => expandAll(expandAllKeys(index, draft.checked, displayRows)),
    [draft.checked, index, displayRows, expandAll]
  );
  const handleCollapseAll = useCallback(
    () => collapseAll(collapseAllKeys(index, draft.checked, draft.expanded)),
    [draft.checked, draft.expanded, index, collapseAll]
  );

  const keepSelection = (op: MemberSubsetOp): void => runOp(op, memberKeys);
  // Ticked ROWS from the grid, by uid: one position can be on screen twice, and only the node tells them apart.
  const removeElements = (): void => {
    const { collapsed, opened } = splitByExpansion(selectedRowUids(gridApiRef.current), expandedRowUids(gridApiRef.current));
    if (collapsed.length > 0) remove(collapsed, true, gridRows);
    if (opened.length > 0) remove(opened, false, gridRows);
  };
  const removeWithDescendants = (): void => remove(selectedRowUids(gridApiRef.current), true, gridRows);
  // Both the draft (what the counts read) and the grid (what moves the ticks) are updated.
  const selectAll = (): void => {
    setChecked(displayRows.map((row) => row.key));
    gridApiRef.current?.selectAll();
  };
  const clearSelected = (): void => {
    setChecked([]);
    gridApiRef.current?.deselectAll();
  };
  const showFlat = (): void => showAll(true);
  // Expansion as STATE: an imperative grid expand ran before the restored rows arrived.
  const showAllMembers = (): void => showAll(false, [...allPositionKeys(index)]);
  const removeAllShown = (): void => removeAll(displayRows.map((row) => row.uid));

  // `null` = the Save dialog is closed.
  const [saveMode, setSaveMode] = useState<SubsetSaveMode | null>(null);
  // Save overwrites the selected view, Save As always creates; both store the rows visible under the expansion.
  // Rethrows, so the dialog can show a duplicate-name 409 inline and stay open.
  const handleSaveAs = useCallback(
    async (viewName: string, visibility: 'PRIVATE' | 'PUBLIC', isDefault: boolean): Promise<void> => {
      // Thrown, not returned: the dialog closes on a resolved promise, so a silent return would read as saved.
      if (aliasPair === undefined) throw new Error('This hierarchy cannot be resolved, so the view cannot be saved.');
      const request = {
        dimension: aliasPair.dimension,
        hierarchy: aliasPair.hierarchy,
        view_name: viewName,
        visibility,
        is_default: isDefault,
        attribute: effectiveAlias,
        entries: entriesToSave(visibleForSave(gridRows, draft.expanded), draft.expanded)
      };
      const overwriting = saveMode === 'save' && viewId !== null;
      const saved = overwriting ? await updateSubsetView(viewId, request) : await createSubsetView(request);
      // Listed without a refetch: an empty or stale dropdown right after saving would read as a failure.
      setViews((current) => [...current.filter((view) => view.id !== saved.id), saved]);
      setViewId(saved.id);
    },
    [aliasPair, effectiveAlias, gridRows, draft.expanded, saveMode, viewId, setViews, setViewId]
  );

  return {
    handleApiReady,
    runOp,
    handleRemove,
    handleAddParents,
    handleExpandAll,
    handleCollapseAll,
    keepSelection,
    removeElements,
    removeWithDescendants,
    selectAll,
    clearSelected,
    showFlat,
    showAllMembers,
    removeAllShown,
    saveMode,
    setSaveMode,
    handleSaveAs
  };
};
